import constants from '@/constants';
import add from 'date-fns/add';
import addMinutes from 'date-fns/addMinutes';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import format from 'date-fns/format';
import isBefore from 'date-fns/isBefore';
import isDate from 'date-fns/isDate';
import parse from 'date-fns/parse';
import parseISO from 'date-fns/parseISO';
import startOfDay from 'date-fns/startOfDay';
import sub from 'date-fns/sub';

import capitalize from 'lodash/capitalize';

import i18n from '@/plugins/i18n';
import isEmpty from 'lodash/isEmpty';
import filter from 'lodash/filter';
import isNull from 'lodash/isNull';
import forEach from 'lodash/forEach';

const DateTimeUtil = {
  is24HourDateTime: false,
  constants: {
    usEnglishLocale: 'en-US',
    isoFormat: "yyyy-MM-dd'T'HH:mm:ss",
    dateFormat: 'yyyy-MM-dd',
    abbrTimeFormat: 'HH:mm',
    clockTimerFormat: 'HH:mm:ss',
    clockTimerTickUnit: 'seconds', // Based on date-fns Duration keys
    clockTimerTickAmount: 1,
    clockTimerTickUnitRefreshRate: 1000,
    timePairDurationUnit: 'seconds',
    timePairDurationRoundingValue: 10,
    minutesInHour: 60,
    badRequestResponseCode: 400,
    unauthorizedResponseCode: 403,
    payCycleDuration: 1,
    payCycleMeasureUnit: 'months',
    midnightTimeStartOfDay: '00:00',
    midnightTimeEndOfDay: '23:59:59',
  },

  validation: {
    missingClockOutThreshold: 5,
    hoursUnit: 'hours',
    minutesUnit: 'minutes',
  },

  dateTime12Hour: {
    locale: i18n.locale,
    timeFormat: 'hh:mm a',
    usDateFormat: {
      dateFormat: 'MM-dd-yyyy',
      dateTimeFormat: 'MM-dd-yyyy hh:mm a',
    },
    intlDateFormat: {
      dateFormat: 'dd-MM-yyyy',
      dateTimeFormat: 'dd-MM-yyyy hh:mm a',
    },
  },

  dateTime24Hour: {
    locale: i18n.locale,
    timeFormat: 'HH:mm',
    usDateFormat: {
      dateFormat: 'MM-dd-yyyy',
      dateTimeFormat: 'MM-dd-yyyy HH:mm',
    },
    intlDateFormat: {
      dateFormat: 'dd-MM-yyyy',
      dateTimeFormat: 'dd-MM-yyyy HH:mm',
    },
  },

  api12HourFormat: {
    locale: i18n.locale,
    usDateFormat: 'MM-dd-YYYY hh:mm a',
    intlDateFormat: 'dd-MM-YYYY hh:mm a',
  },

  api24HourFormat: {
    locale: i18n.locale,
    usDateFormat: 'MM-dd-YYYY HH:mm',
    intlDateFormat: 'dd-MM-YYYY HH:mm',
  },

  switchTimeFormat(timeFormat24Hour) {
    if (timeFormat24Hour) {
      this.dateTime = this.dateTime24Hour;
    } else {
      this.dateTime = this.dateTime12Hour;
    }
    this.is24HourDateTime = timeFormat24Hour;
  },

  is24HourTimeUsed() {
    return (this.dateTime === this.dateTime24Hour);
  },

  isUsLocaleUsed() {
    return this.dateTime.locale.includes(this.constants.usEnglishLocale);
  },

  getLocalDateTime(date, time) {
    return format(new Date(`${date}T${time}`), this.constants.isoFormat);
  },

  convertDateTimeToTime(dateTime) {
    return format(new Date(dateTime), this.constants.abbrTimeFormat);
  },

  convertDateTimeToDate(dateTime) {
    return format(isDate(dateTime) ? dateTime : parseISO(dateTime), this.constants.dateFormat);
  },

  convertToHumanReadableTime(time) {
    const parsedTime = parse(time, this.constants.abbrTimeFormat, new Date());
    return format(parsedTime, this.dateTime.timeFormat);
  },

  convertToHumanReadableDate(dateTime) {
    const { locale } = this.dateTime;
    if (locale.includes(this.constants.usEnglishLocale)) {
      return format(new Date(dateTime), this.dateTime.usDateFormat.dateFormat);
    }

    return format(new Date(dateTime), this.dateTime.intlDateFormat.dateFormat);
  },

  convertTo12HourHumanReadableDateTime(time) {
    return this.convertToHumanReadableDateTime(time, this.dateTime12Hour);
  },

  convertToHumanReadableDateTime(time, dateTimeConfig) {
    const config = dateTimeConfig || this.dateTime;
    if (config.locale.includes(this.constants.usEnglishLocale)) {
      return format(parseISO(time), config.usDateFormat.dateTimeFormat);
    }

    return format(parseISO(time), config.intlDateFormat.dateTimeFormat);
  },

  convertDateTimeToHumanReadableTime(dateTime) {
    if (dateTime) {
      return this.convertToHumanReadableTime(this.convertDateTimeToTime(dateTime));
    }

    return this.$t('misc.none');
  },

  convertToHumanReadableClockOutTime(clockInDateTime, clockOutDateTime) {
    const clockInDate = this.convertDateTimeToDate(clockInDateTime);
    const clockOutDate = this.convertDateTimeToDate(clockOutDateTime);
    const clockOutTime = this.convertDateTimeToHumanReadableTime(clockOutDateTime);

    if (clockInDate !== clockOutDate) {
      return `${clockOutTime} (${DateTimeUtil.convertToHumanReadableDate(clockOutDateTime)})`;
    }

    return clockOutTime;
  },

  getFormattedDuration(durationInMinutes) {
    if (durationInMinutes < 0) {
      return '00:00';
    }

    let hours = Math.floor(durationInMinutes / this.constants.minutesInHour);
    let minutes = Math.floor(durationInMinutes) - hours * this.constants.minutesInHour;
    hours = hours < 10 ? `0${hours}` : hours;
    minutes = minutes < 10 ? `0${minutes}` : minutes;
    return `${hours}:${minutes}`;
  },

  validateTimes(clockInTime, clockOutTime, isShift) {
    const clockInText = isShift ? i18n.t('clock.clockIn') : i18n.t('clock.start');
    const clockOutText = isShift ? i18n.t('clock.clockOut') : i18n.t('clock.end');

    if (!clockInTime) {
      return {
        isError: true,
        message: i18n.t('dialogs.error.invalidTime.message', { time: clockInText }),
        cause: i18n.t('dialogs.error.invalidTime.cause', { time: capitalize(clockInText) }),
      };
    }

    if (!clockOutTime) {
      return {
        isError: true,
        message: i18n.t('dialogs.error.invalidTime.message', { time: clockOutText }),
        cause: i18n.t('dialogs.error.invalidTime.cause', { time: capitalize(clockOutText) }),
      };
    }

    if (clockInTime === clockOutTime) {
      return {
        isError: true,
        message: i18n.t('dialogs.error.sameTime.message', { clockIn: capitalize(clockInText), clockOut: clockOutText }),
        cause: i18n.t('dialogs.error.sameTime.cause', { clockIn: capitalize(clockInText), clockOut: clockOutText }),
      };
    }

    if (clockInTime > clockOutTime) {
      return {
        isError: true,
        message: i18n.t('dialogs.error.clockInAfterClockOut.message',
          { clockIn: capitalize(clockInText), clockOut: clockOutText }),
        cause: i18n.t('dialogs.error.clockInAfterClockOut.cause',
          { clockIn: capitalize(clockInText), clockOut: clockOutText }),
      };
    }

    return {
      isError: false,
    };
  },

  isTimeGreaterThanNow(time) {
    const timestamp = parseISO(time).getTime();
    return timestamp > (new Date()).getTime();
  },

  getStartOfPreviousPayCycle() {
    return sub(
      this.getStartOfCurrentPayCycle(),
      { [this.constants.payCycleMeasureUnit]: this.constants.payCycleDuration },
    );
  },

  getStartOfCurrentPayCycle() {
    let startDay = this.getPayCycleStartDateFromProperties();

    const payCycleDuration = this.constants.payCycleMeasureUnit === 'days' ? {
      days: this.constants.payCycleDuration,
    } : {
      months: this.constants.payCycleDuration,
    };

    while (isBefore(add(startDay, payCycleDuration), new Date())) {
      startDay = add(startDay, payCycleDuration);
    }

    return startDay;
  },

  getPayCycleStartDateFromProperties() {
    return startOfDay(new Date(constants.Properties.PAY_CYCLE_START_DATE));
  },

  isTimeBeforePreviousPayCycle(clockInTime) {
    const clockInTimestamp = parseISO(clockInTime).valueOf();
    return clockInTimestamp < this.getStartOfPreviousPayCycle().valueOf();
  },

  timeEntryOlderThan(timeEntry, threshold, unit) {
    const parsedDateTime = parseISO(timeEntry.date);

    const currentDateTimeInEntryTimeZone = addMinutes(
      new Date(),
      (new Date().getTimezoneOffset() - timeEntry.timeZoneOffset),
    );

    const comparisonDateTime = sub(currentDateTimeInEntryTimeZone, { [unit]: threshold });

    return isBefore(parsedDateTime, comparisonDateTime);
  },

  dateTimeIsGreater(dateTimeA, dateTimeB) {
    if (dateTimeB > dateTimeA) {
      return 1;
    }
    if (dateTimeB < dateTimeA) {
      return -1;
    }
    return 0;
  },

  getDateTimeFormatApplied(userConfig) {
    const dateFormat = userConfig.is24HourDateTimeFormat
      ? this.api24HourFormat
      : this.api12HourFormat;

    if (userConfig && userConfig.locale) {
      const locale = userConfig.locale;
      return locale.includes(this.constants.usEnglishLocale)
        ? dateFormat.usDateFormat
        : dateFormat.intlDateFormat;
    }
    return dateFormat.intlDateFormat;
  },

  getTimerValue(state) {
    let timerValue = DateTimeUtil.getClockTimerInSecondsValue(state.timer);
    if (state.activeSegmentType === constants.TimePairType.SHIFT && !isEmpty(state.activeTimePair.segmentTimePairs)) {
      const segmentPairs = filter(state.activeTimePair.segmentTimePairs, (timePair) => !isNull(timePair.clockOut));
      let accumulatedDuration = 0;
      forEach(segmentPairs, (segmentPair) => {
        console.info(`Found pair ${segmentPair.clockOut.durationAccurateDate}`);
        const duration = differenceInSeconds(
          new Date(segmentPair.clockOut.durationAccurateDate),
          new Date(segmentPair.clockIn.durationAccurateDate),
        );
        accumulatedDuration += duration;
      });
      timerValue = DateTimeUtil.getReducedClockTimerInSecondsValue(timerValue, accumulatedDuration);
    }
    return timerValue;
  },

  getClockTimerInSecondsValue(timer) {
    const parsedTime = isDate(timer.trueClockInTime)
      ? timer.trueClockInTime
      : parseISO(timer.trueClockInTime);

    return differenceInSeconds(
      addMinutes(new Date(), (new Date().getTimezoneOffset() - timer.timeZoneOffset)),
      parsedTime,
    );
  },

  getReducedClockTimerInSecondsValue(timerValue, accumulatedSegmentTimeDuration) {
    return timerValue - accumulatedSegmentTimeDuration;
  },

  incrementClockTimerSecondsValue(timerValue) {
    return timerValue + this.constants.clockTimerTickAmount;
  },

  addMinutesToTime(timeString, minutes) {
    const time = this.getHours(timeString);
    const result = addMinutes(time, minutes);
    return format(result, this.dateTime.timeFormat);
  },

  minutesBetweenTimes(firstTimeString, secondTimeString) {
    const firstTime = this.getHours(firstTimeString);
    const secondTime = this.getHours(secondTimeString);
    return differenceInMinutes(secondTime, firstTime);
  },

  getHoursFromFormatted(timeString) {
    return parse(timeString, this.dateTime.timeFormat, new Date());
  },

  getHours(timeString) {
    return parse(timeString, this.dateTime24Hour.timeFormat, new Date());
  },

  getFormattedHours(time) {
    if (!time) {
      return null;
    }
    return format(parse(
      time.slice(0, 5),
      this.dateTime24Hour.timeFormat, new Date(),
    ), this.dateTime.timeFormat, new Date());
  },

  getFormattedHoursMinutes(date) {
    if (!date) {
      return 'NA';
    }

    const options = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: !this.is24HourDateTime,
    };
    return date.toLocaleTimeString([], options);
  },

  getTimerHmsFormat(duration) {
    let h = Math.floor(duration / 3600);
    let m = Math.floor((duration / 3600 - h) * 60);
    let s = Math.floor(((duration / 3600 - h) * 60 - m) * 60);

    h = h < 10 ? `0${h}` : `${h}`;
    m = m < 10 ? `0${m}` : `${m}`;
    s = s < 10 ? `0${s}` : `${s}`;

    return `${h}:${m}:${s}`;
  },
};

export default DateTimeUtil;
